import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { getSpecialties, createSpecialty, updateSpecialty, deactivateSpecialty } from '../api';
import type { Specialty } from '../types';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { RowActionsMenu } from '@/components/ui/RowActionsMenu';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { toast } from '@/hooks/useToast';
import { SettingsToolbar, filterByStatus, matchesSearch, type StatusFilter } from '../shared/SettingsToolbar';

/**
 * Admin → Clinical → Specialties: the list doctors are assigned to, used to
 * match same-specialty cover during automatic emergency reassignment.
 * List + drawer + ⋯ menu (Admin_Panel_Wireframe.html, D4).
 */
export default function SpecialtiesTab() {
  const [specialties, setSpecialties] = useState<Specialty[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [editing, setEditing] = useState<Specialty | 'new' | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState<Specialty | null>(null);

  const load = useCallback(() => getSpecialties(true).then((s: Specialty[]) => { setSpecialties(s); setLoadError(''); }).catch((e: any) => setLoadError(e.message)), []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try { await fn(); toast({ title: ok, variant: 'success' }); await load(); }
    catch (e: any) { toast({ title: 'Not saved', description: e.message, variant: 'danger' }); }
  };

  const rows = useMemo(
    () => filterByStatus(specialties ?? [], status).filter((s) => matchesSearch(search, s.name)),
    [specialties, status, search]
  );

  const columns: DataTableColumn<Specialty>[] = [
    { key: 'name', header: 'Specialty', sortable: true, sortValue: (s) => s.name, render: (s) => <span className="font-semibold">{s.name}</span> },
    { key: 'status', header: 'Status', widthClassName: 'w-[130px]',
      render: (s) => <Badge variant={s.active ? 'success' : 'neutral'}>{s.active ? 'Active' : 'Inactive'}</Badge> },
    { key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', widthClassName: 'w-[56px]',
      render: (s) => (
        <RowActionsMenu
          label={`Actions for ${s.name}`}
          actions={[
            { label: 'Rename', onSelect: () => setEditing(s) },
            s.active
              ? { label: 'Deactivate…', danger: true, onSelect: () => setConfirmDeactivate(s) }
              : { label: 'Reactivate', onSelect: () => act(() => updateSpecialty(s.id, { active: true }), `${s.name} reactivated`) },
          ]}
        />
      ) },
  ];

  return (
    <div>
      <SettingsToolbar
        search={search}
        onSearch={setSearch}
        placeholder="Search specialties"
        status={status}
        onStatus={setStatus}
        count={specialties ? `${rows.length} specialt${rows.length === 1 ? 'y' : 'ies'}` : undefined}
        action={<Button onClick={() => setEditing('new')}><Plus className="h-4 w-4" /> Add specialty</Button>}
      />
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(s) => s.id}
        isLoading={!specialties && !loadError}
        isError={!!loadError}
        errorMessage={loadError}
        onRetry={load}
        initialSort={{ key: 'name', direction: 'asc' }}
        emptyLabel={search ? 'No specialties match' : status === 'inactive' ? 'No inactive specialties' : 'No specialties yet'}
      />

      <SpecialtySheet
        specialty={editing}
        onClose={() => setEditing(null)}
        onSaved={async (msg) => { setEditing(null); toast({ title: msg, variant: 'success' }); await load(); }}
      />

      <ConfirmDialog
        open={!!confirmDeactivate}
        onOpenChange={(o) => { if (!o) setConfirmDeactivate(null); }}
        title={`Deactivate ${confirmDeactivate?.name ?? ''}?`}
        description="It can no longer be assigned to doctors. Doctors already assigned to it keep it, and you can reactivate it later."
        confirmLabel="Deactivate"
        onConfirm={() => { const s = confirmDeactivate; if (s) act(() => deactivateSpecialty(s.id), `${s.name} deactivated`); }}
      />
    </div>
  );
}

function SpecialtySheet({ specialty, onClose, onSaved }: { specialty: Specialty | 'new' | null; onClose: () => void; onSaved: (msg: string) => void }) {
  const isNew = specialty === 'new';
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!specialty) return;
    setError(''); setTouched(false);
    setName(specialty === 'new' ? '' : specialty.name);
  }, [specialty]);

  const save = async () => {
    setTouched(true);
    const n = name.trim();
    if (!n) return;
    setSaving(true); setError('');
    try {
      if (isNew) { await createSpecialty({ name: n }); onSaved(`${n} added`); }
      else if (specialty) { await updateSpecialty(specialty.id, { name: n }); onSaved(`Renamed to ${n}`); }
    } catch (e: any) { setError(e.message); }
    setSaving(false);
  };

  return (
    <Sheet open={!!specialty} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{isNew ? 'Add specialty' : 'Rename specialty'}</SheetTitle>
          <SheetDescription>Doctors are assigned a specialty in Users &amp; Roles.</SheetDescription>
        </SheetHeader>
        <SheetBody>
          {error && <div className="rounded-md bg-danger-soft px-3 py-2 text-[12.5px] text-danger-ink">{error}</div>}
          <FormField label="Name" required error={touched && !name.trim() ? 'Enter a name' : undefined}>
            <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); }} placeholder="e.g. Cardiology" autoFocus />
          </FormField>
        </SheetBody>
        <SheetFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={saving}>{isNew ? 'Add specialty' : 'Save'}</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
